import styled from '@emotion/styled';

import { focusRing, forMobile, hoverable, scrollbarStyles, theme } from '@/design-system';
import { typographyVariantStyles } from '@/design-system/typography-variants';

const STAT_CARDS_PER_ROW = 4;
const STAT_CARD_GAP = theme.spacing[8];
const STAT_CARD_MIN_WIDTH = 148;

export const TabRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
  paddingTop: theme.spacing[16],
  minWidth: 0,
});

export const FilterBar = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: theme.spacing[8],

  [forMobile]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

export const FilterSlot = styled.div({
  width: 200,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
  },
});

export const ManagerSlot = styled.div({
  width: 260,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
  },
});

export const RangeSlot = styled.div({
  width: 280,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
  },
});

export const Section = styled.section({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  minWidth: 0,
});

export const SectionHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minHeight: 32,
});

export const SectionActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[12],
  flexShrink: 0,
});

export const StatCardRow = styled.div({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(max(${STAT_CARD_MIN_WIDTH}px, calc((100% - ${(STAT_CARDS_PER_ROW - 1) * STAT_CARD_GAP}px) / ${STAT_CARDS_PER_ROW})), 1fr))`,
  gap: STAT_CARD_GAP,
});

const statCardBase = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing[4],
  minHeight: 92,
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  border: '1px solid transparent',
  backgroundColor: theme.colors.background.raised,
  minWidth: 0,
  textAlign: 'left',
} as const;

export const StatCard = styled.div(statCardBase);

export const StatCardButton = styled.button({
  ...statCardBase,
  margin: 0,
  fontFamily: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'border'),
  ...focusRing(theme),

  '&[aria-pressed="true"]': {
    borderColor: theme.colors.border.brand,
    backgroundColor: theme.colors.brandAlpha[8],
  },
});

export const StatCardLabel = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[6],
  color: theme.colors.text.secondary,
  minWidth: 0,
});

export const ContactList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
});

export const ContactCard = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  padding: `${theme.spacing[12]}px ${theme.spacing[16]}px`,
  borderRadius: theme.radius[12],
  border: `1px solid ${theme.colors.border.subtle}`,
  backgroundColor: theme.colors.background.raised,
  minWidth: 0,
});

export const ContactHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[12],
  minWidth: 0,

  [forMobile]: {
    alignItems: 'flex-start',
  },
});

export const ContactTitle = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  flex: 1,
  minWidth: 0,

  '& > a': {
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
});

export const ContactMeta = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const ContactEntries = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  paddingLeft: theme.spacing[32],

  [forMobile]: {
    paddingLeft: 0,
  },
});

export const EntryMetaRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const EntryText = styled.div({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.primary,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
});

export const QuietLine = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing[8],
  paddingTop: theme.spacing[16],
  paddingBottom: theme.spacing[16],
  textAlign: 'center',
});

export const SkeletonBar = styled.div<{ w?: string; h?: number }>(({ w, h }) => ({
  height: h ?? theme.spacing[12],
  width: w ?? '100%',
  borderRadius: theme.radius[4],
  background: theme.colors.background.overlay,
  animation: 'gpTeamActivityPulse 1.4s ease-in-out infinite',
  '@keyframes gpTeamActivityPulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 1 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const MemberPickerPositioner = styled.div({
  zIndex: 1400,
});

export const MemberPickerPopup = styled.div({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  width: 'max(var(--anchor-width), 280px)',
  maxWidth: 'var(--available-width)',
  maxHeight: 'min(420px, var(--available-height))',
  borderRadius: theme.radius[8],
  border: `1px solid ${theme.colors.border.default}`,
  backgroundColor: theme.colors.background.raised,
  boxShadow: theme.shadows.md,
  outline: 'none',
  transformOrigin: 'var(--transform-origin)',
  transition: 'opacity 0.15s ease, transform 0.15s ease',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.97) translateY(-4px)',
  },
});

export const MemberPickerSearch = styled.div({
  flexShrink: 0,
  padding: theme.spacing[8],
  borderBottom: `1px solid ${theme.colors.border.subtle}`,
});

export const MemberPickerList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing[8],
  ...scrollbarStyles,
});

export const MemberPickerRow = styled.button({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  minHeight: 36,
  margin: 0,
  padding: `${theme.spacing[6]}px ${theme.spacing[8]}px`,
  border: 'none',
  borderRadius: theme.radius[8],
  backgroundColor: 'transparent',
  color: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'surface'),
  ...focusRing(theme),

  '&[aria-checked="true"]': {
    backgroundColor: theme.colors.brandAlpha[8],
  },
});

export const MemberPickerRowText = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
});
